/**
 * Revenue Analytics dashboard component.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type RevenueMetrics, type ChannelBreakdown, type RevenueWebsite } from '../api';
import { Notice } from './Panels';
import { ImportRevenueDialog } from './ImportRevenueDialog';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function RevenueAnalytics() {
  const [websites, setWebsites] = useState<RevenueWebsite[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<string>('');
  const [metrics, setMetrics] = useState<RevenueMetrics | null>(null);
  const [channels, setChannels] = useState<ChannelBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [dateRange, setDateRange] = useState('30d');
  const [showImport, setShowImport] = useState(false);

  const loadWebsites = useCallback(async () => {
    try {
      const data = await api.listRevenueWebsites();
      setWebsites(data.websites);
      if (data.websites.length > 0) {
        setSelectedWebsite((current) => current === '' ? data.websites[0]!.id : current);
      }
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    }
  }, []);

  useEffect(() => { void loadWebsites(); }, [loadWebsites]);

  const loadMetrics = useCallback(async () => {
    if (selectedWebsite === '') return;
    setLoading(true);
    setFailure(null);
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const [metricsData, channelsData] = await Promise.all([
        api.getRevenueMetrics({ websiteId: selectedWebsite, startDate, endDate }),
        api.getRevenueChannelBreakdown({ websiteId: selectedWebsite, startDate, endDate }),
      ]);
      setMetrics(metricsData.metrics);
      setChannels(channelsData.breakdown);
    } catch (error) {
      setMetrics(null);
      setChannels([]);
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setLoading(false);
    }
  }, [selectedWebsite, dateRange]);

  useEffect(() => { void loadMetrics(); }, [loadMetrics]);

  if (websites.length === 0) {
    return (
      <div className="page">
        <div className="card empty">
          <h3>No websites configured</h3>
          <p>
            Create a website to start tracking revenue and attribution — go to Websites in the sidebar —
            or import sales history from an uploaded CSV.
          </p>
          <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
            <button className="primary" onClick={() => setShowImport(true)}>
              Import from uploaded dataset
            </button>
          </div>
        </div>
        {showImport && (
          <ImportRevenueDialog
            onClose={() => setShowImport(false)}
            onImported={() => { setShowImport(false); void loadWebsites(); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <header className="intro">
        <h2>Revenue Analytics</h2>
        <p>Track revenue, attribution, and marketing ROI across all channels.</p>
      </header>

      <div className="controls">
        <label className="field">
          <span>Website</span>
          <select value={selectedWebsite} onChange={(e) => setSelectedWebsite(e.target.value)}>
            {websites.map((w) => (
              <option key={w.id} value={w.id}>{w.domain}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Period</span>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="12m">Last 12 months</option>
          </select>
        </label>
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {loading && <div className="card"><p className="muted"><span className="spinner" /> Loading metrics…</p></div>}

      {!loading && metrics !== null && (
        <>
          <section className="scorecards">
            <MetricCard title="Gross Revenue" value={formatCurrency(metrics.grossRevenue)} subtitle="Before refunds" />
            <MetricCard title="Net Revenue" value={formatCurrency(metrics.netRevenue)} subtitle="After refunds" />
            <MetricCard title="MRR" value={formatCurrency(metrics.mrr)} subtitle="Monthly recurring" />
            <MetricCard title="ARR" value={formatCurrency(metrics.arr)} subtitle="Annual run rate" />
            <MetricCard title="LTV" value={formatCurrency(metrics.ltv)} subtitle="Customer lifetime value" />
            <MetricCard title="CAC" value={formatCurrency(metrics.cac)} subtitle="Acquisition cost" />
            <MetricCard title="ROAS" value={metrics.roas !== null ? `${metrics.roas.toFixed(1)}x` : 'N/A'} subtitle="Return on ad spend" />
            <MetricCard title="Conversion Rate" value={formatPercent(metrics.conversionRate)} subtitle="Visitors to customers" />
            <MetricCard title="Revenue / Visitor" value={formatCurrency(metrics.revenuePerVisitor)} subtitle="Average per visitor" />
            <MetricCard title="Avg Order Value" value={formatCurrency(metrics.averageOrderValue)} subtitle="Per transaction" />
            <MetricCard title="New Customer Revenue" value={formatCurrency(metrics.newCustomerRevenue)} subtitle="First-time buyers" />
            <MetricCard title="Returning Revenue" value={formatCurrency(metrics.returningCustomerRevenue)} subtitle="Repeat customers" />
          </section>

          {channels.length > 0 && (
            <section className="card">
              <header><h2>Revenue by Channel</h2></header>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th style={{ textAlign: 'right' }}>Visitors</th>
                    <th style={{ textAlign: 'right' }}>Conversions</th>
                    <th style={{ textAlign: 'right' }}>Revenue</th>
                    <th style={{ textAlign: 'right' }}>Spend</th>
                    <th style={{ textAlign: 'right' }}>ROAS</th>
                    <th style={{ textAlign: 'right' }}>Rev/Visitor</th>
                    <th style={{ textAlign: 'right' }}>Conv Rate</th>
                    <th style={{ textAlign: 'right' }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((ch) => (
                    <tr key={ch.channel}>
                      <td style={{ fontWeight: 500 }}>{ch.channel}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(ch.visitors)}</td>
                      <td style={{ textAlign: 'right' }}>{formatNumber(ch.conversions)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(ch.revenue)}</td>
                      <td style={{ textAlign: 'right' }}>{ch.spend > 0 ? formatCurrency(ch.spend) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{ch.spend > 0 ? `${(ch.revenue / ch.spend).toFixed(2)}x` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(ch.revenuePerVisitor)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPercent(ch.conversionRate)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPercent(ch.attributionShare)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function MetricCard({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
  return (
    <div className="scorecard">
      <div className="label">{title}</div>
      <div className="value">{value}</div>
      <div className="meta">{subtitle}</div>
    </div>
  );
}
